import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// In-flight request de-duplication: on slow networks a double-click / retry can
// fire the same mutating request twice and create duplicate records (or duplicate
// file uploads). If an identical request is already in flight, reuse its promise
// instead of sending a second one. Handles both JSON bodies and FormData uploads.
const _inflight = new Map();
const _reqKey = (method, url, data) => {
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    const parts = [];
    for (const [k, v] of data.entries()) parts.push(k + "=" + (v instanceof File ? `${v.name}:${v.size}:${v.lastModified}` : v));
    return `${method} ${url} FD[${parts.join("&")}]`;
  }
  return `${method} ${url} ${data ? JSON.stringify(data) : ""}`;
};
["post", "put", "patch", "delete"].forEach((method) => {
  const orig = api[method].bind(api);
  api[method] = (url, ...rest) => {
    const data = method === "delete" ? undefined : rest[0];
    const key = _reqKey(method, url, data);
    if (_inflight.has(key)) return _inflight.get(key);
    const promise = orig(url, ...rest).finally(() => _inflight.delete(key));
    _inflight.set(key, promise);
    return promise;
  };
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("daa_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401 && !window.location.pathname.startsWith("/login")) {
      localStorage.removeItem("daa_token");
      window.location.href = "/login";
    }
    // Normalize FastAPI/Pydantic error detail into a readable STRING so no caller
    // ever renders a raw error object/array as a React child (crashes the app).
    const d = error.response?.data;
    if (d && d.detail != null && typeof d.detail !== "string") {
      const toMsg = (it) => {
        if (it == null) return "";
        if (typeof it === "string") return it;
        if (typeof it === "object") {
          const loc = Array.isArray(it.loc) ? it.loc.filter((x) => x !== "body").join(".") : "";
          return (loc ? `${loc}: ` : "") + (it.msg || it.message || JSON.stringify(it));
        }
        return String(it);
      };
      d.detail = Array.isArray(d.detail) ? d.detail.map(toMsg).filter(Boolean).join("; ") : toMsg(d.detail);
    }
    // System-wide: a rule/guard rejection (400 on a write) is shown in the "Action Blocked" dialog.
    const m = (error.config?.method || "").toLowerCase();
    if (error.response?.status === 400 && typeof d?.detail === "string" && m && m !== "get" && !error.config?.skipBlocked) {
      error.blockedShown = true;
      window.dispatchEvent(new CustomEvent("daa-action-blocked", { detail: d.detail }));
    }
    return Promise.reject(error);
  }
);

export const fmtMoney = (n) =>
  new Intl.NumberFormat("en-MY", { style: "currency", currency: "MYR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export const fmtProjNo = (n) => (n == null || n === "" ? "—" : String(n).padStart(3, "0"));

export const riskColor = (r) =>
  ({ green: "text-emerald-500", yellow: "text-amber-500", red: "text-red-500" }[r] || "text-muted-foreground");

export const riskBg = (r) =>
  ({
    green: "bg-emerald-500/12 text-emerald-500 border-emerald-500/30",
    yellow: "bg-amber-500/12 text-amber-500 border-amber-500/30",
    red: "bg-red-500/12 text-red-500 border-red-500/30",
  }[r] || "bg-muted text-muted-foreground");

export const riskLabel = (r) => ({ green: "Low", yellow: "Medium", red: "High" }[r] || r);

// Account-role display labels (internal keys stay unchanged for data/permissions)
export const ACCOUNT_ROLE_LABELS = { Consultant: "Project Consultant", Designer: "Project Designer" };
export const accountRoleLabel = (r) => ACCOUNT_ROLE_LABELS[r] || r || "—";

export const PROJECT_TYPES = ["Design Only", "Build Only", "Design & Build"];
export const CLIENT_TITLES = ["Mr", "Mrs", "Ms", "Dr", "Datuk", "Dato'", "Datin", "Tan Sri", "Puan Sri", "Encik", "Puan"];
export const hasDesign = (t) => t !== "Build Only";
export const hasBuild = (t) => t !== "Design Only";
export const typeBadge = (t) =>
  ({
    "Design Only": "bg-violet-500/12 text-violet-400 border-violet-500/30",
    "Build Only": "bg-sky-500/12 text-sky-400 border-sky-500/30",
    "Design & Build": "bg-primary/12 text-primary border-primary/30",
  }[t] || "bg-muted text-muted-foreground border-border");

export const STATUS_COLORS = {
  "Project Ongoing": "#3b82f6",
  "Defect Rectification": "#f97316",
  "Pending Final Payment": "#f59e0b",
  "Pending Project Closing": "#06b6d4",
  "Project Completed": "#10b981",
  "Completed · Within DLP": "#10b981",
  "Completed · DLP Ended": "#64748b",
  "Keep In View": "#a855f7",
  "Withdrawn by DAA": "#71717a",
  "Rejected / Cancelled by Client": "#ef4444",
};

export const STATUSES = [
  "Project Ongoing", "Defect Rectification", "Pending Final Payment", "Pending Project Closing", "Project Completed",
  "Keep In View", "Withdrawn by DAA", "Rejected / Cancelled by Client",
];
export const STATUS_CODES = {
  "Project Ongoing": "OG", "Defect Rectification": "DE", "Pending Final Payment": "PY", "Pending Project Closing": "PC",
  "Project Completed": "CP", "Keep In View": "KV", "Withdrawn by DAA": "WD",
  "Rejected / Cancelled by Client": "RJ",
};

export const LEAD_STATUSES = ["Ongoing", "Awarded", "Rejected / Cancelled by Client", "Missing", "Withdrawn by DAA", "Keep In View"];
export const LEAD_STATUS_CODES = { "Ongoing": "OG", "Awarded": "AD", "Rejected / Cancelled by Client": "RJ", "Withdrawn by DAA": "WD", "Keep In View": "KV" };
export const LEAD_STATUS_COLORS = { "Ongoing": "#3b82f6", "Awarded": "#10b981", "Rejected / Cancelled by Client": "#ef4444", "Missing": "#71717a", "Withdrawn by DAA": "#71717a", "Keep In View": "#a855f7" };
export const UNIT_TYPES = ["Condo", "Landed", "Apartment", "Office", "Retail", "Other"];
export const fmtLeadNo = (n) => (n == null || n === "" ? "—" : String(n));

const _MYT_FMT = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kuala_Lumpur", year: "numeric", month: "2-digit", day: "2-digit" });
// Today's business date (YYYY-MM-DD) in Malaysia time.
export const todayISO = () => _MYT_FMT.format(new Date());
// YYYY-MM-DD of a Date built from local calendar parts (e.g. new Date(`${iso}T00:00:00`)).
export const localISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const _addMonthsIso = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setMonth(d.getMonth() + n); return localISO(d); };
// DLP sub-state for a Project Completed project. Returns null when not applicable
// (status != Project Completed, or no DLP / no Practical Completion — e.g. design-only).
export const dlpInfo = (p) => {
  if (!p || p.status !== "Project Completed") return null;
  const wf = p.construction_workflow || {};
  const pc = p.actual_completion_date;
  const end = (wf.dlp || {}).end_date || (pc ? _addMonthsIso(pc, 12) : null);
  if (!end) return null;
  const today = todayISO();
  const ended = today > end;
  const _dlpStage = (d) => { let s = d.stage || (d.rectified ? "rectified" : "raised"); return s === "investigate" ? "solution" : s; };
  const openDefects = ((wf.owner_defects) || []).filter((d) => { const s = _dlpStage(d); return s !== "rectified" && s !== "closed"; }).length;
  return { ended, end, openDefects, label: ended ? "DLP Ended" : "Within DLP" };
};
// Defect at-a-glance badge: red if any open defect shows a red status (raised "Open" or SLA-overdue), else amber.
export const defectBadge = (p) => {
  const wf = (p && p.construction_workflow) || {};
  const list = wf.owner_defects || [];
  const stg = (d) => { let s = d.stage || (d.rectified ? "rectified" : "raised"); return s === "investigate" ? "solution" : s; };
  const isOpen = (d) => { const s = stg(d); return s !== "rectified" && s !== "closed"; };
  const today = todayISO();
  const age = (d) => Math.round((new Date(`${today}T00:00:00`) - new Date(`${(d.date_raised || d.stage_since)}T00:00:00`)) / 86400000);
  const sla = (d) => { const s = stg(d); const base = (s === "raised" || s === "solution") ? 3 : null; return base == null ? null : (d.urgent ? 1 : base); };
  const overdue = (d) => { const sl = sla(d); return sl != null && isOpen(d) && (d.date_raised || d.stage_since) && age(d) > sl; };
  const open = list.filter(isOpen);
  if (open.length === 0) return null;
  const red = open.some((d) => d.urgent || overdue(d));
  return { tone: red ? "red" : "amber", count: open.length };
};
export const HOLD_CATEGORIES = [
  "Client Unresponsive", "Pending Client Confirmation", "Outstanding Payment", "Client Requested Hold",
  "Contractual Issue", "Site Issue", "Authority Issue", "Technical Issue", "Commercial Issue",
  "Internal Management Review", "Risk Control", "Others",
];

export function exportCSV(rows, filename) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
